import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, ScanLine, FileCheck2, Building2, ArrowRight } from "lucide-react";
import { API } from "../lib/api";
import { MunculSaatTerlihat } from "../components/ScrollDepth";

const LANGKAH = [
  ["01", "Kumpulkan daftar akun", "Ekspor daftar akun dari tiap sistem, lalu cocokkan dengan daftar orang yang masih bekerja."],
  ["02", "Buka atau tutup", "Cuma dua pilihan. Buka kalau memberi akses, tutup kalau mencabut. Tidak ada jalan tengah."],
  ["03", "Pemilik akses menutup", "Yang memegang akses itu menutup sendiri, lalu menulis bukti singkat. Waktunya diambil dari server."],
  ["04", "Surat terbit", "Kalau semua akses orang itu sudah tertutup, surat bebas akses terbit otomatis — bernomor dan bisa dicek siapa saja."],
];

const NILAI = [
  [ShieldCheck, "Di luar sistem perusahaan", "Catatannya tidak disimpan di dalam sistem yang sedang dinilai. Jadi tidak ada yang bisa merapikan riwayat sendiri."],
  [ScanLine, "Punya sidik digital", "Tiap surat punya nomor dan sidik digital. Isinya berubah sedikit saja, sidiknya ikut berubah dan langsung kelihatan."],
  [FileCheck2, "Bisa dicek siapa saja", "Perusahaan lain, auditor, atau calon pemberi kerja bisa memeriksa surat tanpa perlu akun dan tanpa melihat data pribadi."],
];

export default function Landing() {
  const [ring, setRing] = useState(null);
  const [nomor, setNomor] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    API.get("/demo/ringkasan").then((r) => setRing(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen flex flex-col overflow-x-clip">
      <header className="kaca border-b sticky top-0 z-30">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <ShieldCheck size={18} className="text-primary" aria-hidden="true" />
            <span className="font-heading font-bold text-lg">PAM-IT</span>
            <span className="hidden sm:inline text-xs text-muted-foreground truncate">
              Pencatatan Akses Management - IT
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/verifikasi" data-testid="tautan-verifikasi"
              className="hidden sm:inline-block px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors duration-150">
              Cek surat
            </Link>
            <Link to="/masuk" data-testid="tautan-masuk" className="tombol-garis px-3 py-1.5 text-sm rounded">
              Masuk
            </Link>
            <Link to="/daftar" data-testid="tautan-daftar" className="tombol-utama px-3 py-1.5 text-sm rounded">
              Daftar
            </Link>
          </div>
        </div>
      </header>

      <section className="relative">
        <div className="absolute inset-0 kisi-halus -z-10" aria-hidden="true" />
        <div className="max-w-[1200px] mx-auto px-4 md:px-8 pt-20 pb-24 md:pt-28 md:pb-32">
          <MunculSaatTerlihat testid="hero-pam-it" className="max-w-3xl">
            <p className="label-kecil">Platform netral · Pencatatan Akses Management - IT</p>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05]">
              Orangnya sudah pergi.
              <span className="block text-primary">Aksesnya jangan ikut tinggal.</span>
            </h1>
            <p className="mt-7 text-base md:text-lg text-muted-foreground max-w-2xl leading-relaxed">
              PAM-IT mencatat setiap akses yang dibuka dan ditutup, lalu menerbitkan surat bebas akses
              yang bisa dicek keasliannya oleh siapa pun. Karena catatannya ada di luar sistem perusahaan,
              hasilnya bisa dipertanggungjawabkan.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <button data-testid="btn-buka-demo" onClick={() => nav("/demo")}
                className="tombol-utama px-6 py-3 rounded text-base">
                Lihat contoh datanya
              </button>
              <Link to="/minta" data-testid="tautan-minta" className="tombol-garis px-6 py-3 rounded text-base">
                Catat perubahan akses
              </Link>
              <Link to="/daftar" data-testid="tautan-daftar-hero" className="tombol-garis px-6 py-3 rounded text-base">
                Daftarkan perusahaan saya
              </Link>
            </div>
          </MunculSaatTerlihat>

          <MunculSaatTerlihat delay={0.12} className="mt-16 md:mt-20">
            <div className="panel rounded p-6 md:p-8 max-w-2xl">
              <p className="label-kecil">Cek keaslian surat</p>
              <p className="mt-3 text-sm text-muted-foreground">
                Punya Surat Keterangan Bebas Akses? Masukkan nomornya, kami tampilkan apakah surat itu benar terbit dari sini.
              </p>
              <form className="mt-5 flex flex-col sm:flex-row gap-2"
                onSubmit={(e) => { e.preventDefault(); if (nomor.trim()) nav(`/verifikasi/${encodeURIComponent(nomor.trim())}`); }}>
                <input data-testid="input-cek-beranda" value={nomor} onChange={(e) => setNomor(e.target.value)}
                  aria-label="Nomor surat" placeholder="SKBA-2026-0001"
                  className="flex-1 border rounded px-3 py-2.5 text-sm font-data" />
                <button data-testid="btn-cek-beranda" className="tombol-utama px-5 py-2.5 rounded text-sm inline-flex items-center justify-center gap-2">
                  Periksa <ArrowRight size={15} />
                </button>
              </form>
            </div>
          </MunculSaatTerlihat>

          {ring && (
            <MunculSaatTerlihat delay={0.06} className="mt-16">
              <div className="garis-cahaya mb-8" />
              <p data-testid="kalimat-temuan" className="text-lg md:text-xl max-w-3xl leading-relaxed">
                Di data contoh kami, <span className="font-data font-bold text-accent">{ring.tak_bertuan}</span> akun
                masih hidup padahal orangnya sudah keluar. Yang paling lama sudah{" "}
                <span className="font-data font-bold text-accent">{ring.tertua_hari}</span> hari "idle" tanpa pemilik.
              </p>
            </MunculSaatTerlihat>
          )}
        </div>
      </section>

      <section className="border-t">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8 py-20">
          <MunculSaatTerlihat>
            <p className="label-kecil">Kenapa ini beda</p>
            <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-bold max-w-2xl">
              Bukti akses seharusnya tidak diurus oleh pihak yang dinilai
            </h2>
          </MunculSaatTerlihat>
          <div className="mt-12 grid gap-4 md:grid-cols-3">
            {NILAI.map(([Ikon, judul, isi], i) => (
              <MunculSaatTerlihat key={judul} delay={i * 0.08}>
                <div className="panel panel-hover rounded p-6 h-full" data-testid={`nilai-${i}`}>
                  <Ikon size={22} className="text-primary" aria-hidden="true" />
                  <h3 className="mt-4 text-lg font-semibold">{judul}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{isi}</p>
                </div>
              </MunculSaatTerlihat>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8 py-20">
          <MunculSaatTerlihat>
            <p className="label-kecil">Cara kerjanya</p>
            <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-bold">Empat langkah, tidak ada yang tersembunyi</h2>
          </MunculSaatTerlihat>
          <ol className="mt-12 grid gap-4 md:grid-cols-2" data-testid="alur-langkah">
            {LANGKAH.map(([n, t, d], i) => (
              <MunculSaatTerlihat key={n} delay={i * 0.07}>
                <li className="panel panel-hover rounded p-6 h-full flex gap-5">
                  <span className="font-data text-2xl font-bold text-primary/70">{n}</span>
                  <div>
                    <h3 className="text-lg font-semibold">{t}</h3>
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{d}</p>
                  </div>
                </li>
              </MunculSaatTerlihat>
            ))}
          </ol>
        </div>
      </section>

      <section className="border-t">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8 py-20">
          <MunculSaatTerlihat>
            <p className="label-kecil">Buat siapa</p>
            <div className="mt-8 grid gap-4 md:grid-cols-3">
              {[
                ["Tim IT", "Tahu persis akun mana yang harus ditutup hari ini, tanpa utak-atik berkas ekspor."],
                ["HR", "Bisa memastikan orang yang pamit benar-benar sudah bersih sebelum berkasnya ditutup."],
                ["Penerima surat", "Perusahaan atau auditor bisa memeriksa surat lewat nomornya, tanpa perlu percaya begitu saja."],
              ].map(([t, d]) => (
                <div key={t} className="panel rounded p-6">
                  <Building2 size={18} className="text-accent" aria-hidden="true" />
                  <h3 className="mt-4 text-lg font-semibold">{t}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{d}</p>
                </div>
              ))}
            </div>
          </MunculSaatTerlihat>
        </div>
      </section>

      <footer className="mt-auto border-t">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8 py-8 flex flex-wrap items-center justify-between gap-4 text-xs text-muted-foreground">
          <span>PAM-IT — Pencatatan Akses Management - IT</span>
          <div className="flex gap-4">
            <Link to="/demo" className="hover:text-foreground transition-colors duration-150">Data contoh</Link>
            <Link to="/bukti-dampak" className="hover:text-foreground transition-colors duration-150">Bukti dampak</Link>
            <Link to="/verifikasi" className="hover:text-foreground transition-colors duration-150">Cek surat</Link>
            <Link to="/minta" className="hover:text-foreground transition-colors duration-150">Catat akses</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
